import React from 'react';
import { Link } from '@tanstack/react-router';
import {
  FileText, Video, Table, Image as ImageIcon,
  Music, Presentation, Archive, Code, AppWindow, Lock,
  BookOpen, GraduationCap, MessageSquare, MessagesSquare, UserRound,
} from 'lucide-react';
import type { Components } from 'react-markdown';
import { Markdown } from '@/components/shared/Markdown';
import type { ResolvedCitation } from '@/api/alfie';

const ASSET_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  video: Video,
  spreadsheet: Table,
  presentation: Presentation,
  image: ImageIcon,
  audio: Music,
  archive: Archive,
  code: Code,
  smart_app: AppWindow,
};

/** Minimal shape of a library asset used only by the legacy name-search
 *  fallback (a message with no resolved citations — old rows, community
 *  Alfie replies). Structurally satisfied by the full `Asset` type. */
interface AssetRef {
  title: string;
  thumbnail?: string;
  type: string;
  category: string;
}

// ─── Citation token grammar (Citation Wire Contract v2) ──────
// A message's model text can contain three kinds of `[[…]]` marker:
//
//  1. TYPED   `[[<alias>:<uuid>|<label>]]` — the v2 canonical form. The label
//     is optional (python always writes it; we tolerate its absence). Aliases
//     map to entity types (below). Typed tokens resolve against the resolved
//     citation list by (entityType, entityId) — NEVER by name.
//  2. CITE    `[[cite:N]]` — a transitional token the model emits mid-stream.
//     The server rewrites these into typed tokens before it persists / emits
//     `done`. The FE renders them as nothing (never shows the raw token).
//  3. LEGACY  `[[Name]]` — the pre-v2 form. Kept EXACTLY as before: name match
//     against citations, then the library name-search fallback (with the
//     web-address guard). Only used when the message has NO typed token.
//
// Mode is decided per message: if the content contains at least one typed
// token, the message is in v2 mode and NONE of its tokens use the legacy
// fallback — an unmatched / non-typed token renders as plain text instead.

/** Alias → entity type. Aliases are the short prefixes python writes; the
 *  values are the `entityType` hermes stamps on each resolved citation. */
const ALIAS_TO_ENTITY_TYPE: Record<string, string> = {
  asset: 'asset',
  course: 'course',
  lesson: 'lesson_transcript',
  post: 'community_post',
  event: 'event_transcript',
  chat: 'chat_window',
  expert: 'expert',
};

/** entityType → short type word shown inside the citation chip. The resource
 *  TITLE stays in the surrounding prose (the model names it there); the chip
 *  never repeats it — that duplication was the pre-chip rendering bug. */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
  asset: 'Asset',
  course: 'Course',
  lesson_transcript: 'Lesson',
  community_post: 'Post',
  event_transcript: 'Event',
  chat_window: 'Chat',
  expert: 'Expert',
};

export const ENTITY_TYPE_CHIP_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  asset: FileText,
  course: BookOpen,
  lesson_transcript: GraduationCap,
  community_post: MessageSquare,
  event_transcript: Video,
  chat_window: MessagesSquare,
  expert: UserRound,
};

const ALIAS_ALT = Object.keys(ALIAS_TO_ENTITY_TYPE).join('|');
const UUID_SRC = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

// Outer marker: `[[ … ]]` with surrounding whitespace trimmed off the inner.
// Non-greedy so it stops at the first `]]`; `.` never crosses a newline.
const CITATION_RE = /\[\[\s*(.+?)\s*\]\]/g;
// Transitional streaming token — rendered invisible.
const CITE_INNER_RE = /^cite:\s*\d+$/i;
// Typed token inner: `alias:uuid` with an optional `|label` tail. The label
// captures everything up to the closing `]]`, so pipes / emoji / punctuation
// inside it are preserved and can never affect matching (we match by uuid).
const TYPED_INNER_RE = new RegExp(`^(${ALIAS_ALT}):(${UUID_SRC})(?:\\|([\\s\\S]*))?$`, 'i');
// Does a whole message contain at least one typed token? Decides v2 vs legacy
// mode. No `g` flag — used only with `.test`, so it stays stateless.
const HAS_TYPED_TOKEN_RE = new RegExp(`\\[\\[\\s*(?:${ALIAS_ALT}):${UUID_SRC}`, 'i');

/** Where a citation url may point. Anything else (javascript:, data:, a bare
 *  word, …) is treated as non-linkable and rendered as an inert chip. */
function classifyUrl(url: string | null | undefined): 'internal' | 'external' | 'none' {
  if (!url) return 'none';
  const u = url.trim();
  if (u.startsWith('/')) return 'internal';
  const lower = u.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return 'external';
  return 'none';
}

/** True when a citation name is a web address rather than a content title —
 *  e.g. `pay.com.au` or `https://foo.com/bar`. The model sometimes wraps URLs
 *  it saw inside community content in citation tokens; those must never fall
 *  through to the /assets?search= link (the target is not a library asset). */
function looksLikeWebAddress(name: string): boolean {
  const v = name.trim();
  if (/^https?:\/\//i.test(v)) return true;
  if (/\s/.test(v)) return false;
  return /^[\w-]+(\.[\w-]+)*\.[a-z]{2,6}(\/\S*)?$/i.test(v);
}

// ─── Inline asset link (legacy name-search fallback ONLY) ────
/** Renders a clickable /assets?search=<title> link. Used only when a `[[Name]]`
 *  token has no matching resolved citation (legacy rows / community replies). */
function AssetInlineLink({ name, asset }: { name: string; asset?: AssetRef }) {
  const FallbackIcon = asset ? (ASSET_TYPE_ICONS[asset.type] || FileText) : FileText;
  const thumbnail = asset?.thumbnail;
  const searchTerm = asset?.title ?? name;
  return (
    <Link
      to="/assets"
      search={{ search: searchTerm }}
      className="inline-flex items-center gap-1.5 align-middle text-primary hover:text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary/60 transition-colors"
    >
      {thumbnail ? (
        <img src={thumbnail} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0 inline-block" />
      ) : (
        <FallbackIcon className="w-4 h-4 flex-shrink-0 inline-block" />
      )}
      {name}
    </Link>
  );
}

// ─── Chat-scale element renderers (design "1b") ──────────────
// The full-page Alfie chat reads at a larger scale than the shared Markdown
// defaults: 15px/1.66 body with 16px bold section heads, so headings are
// distinguishable from body text. Ordered lists render as step rows with
// numbered badges (`.alfie-chat-steps`, styled in globals.css). Only applied
// when the consumer opts in via `variant="chat"` — the side panel and
// community surfaces keep the shared defaults.
const CHAT_SCALE_COMPONENTS: Components = {
  h1: ({ children }) => (
    <h1 className="mb-1.5 mt-4 text-[17px] font-bold tracking-[-0.01em] text-foreground first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-1.5 mt-4 text-base font-bold tracking-[-0.01em] text-foreground first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1 mt-3 text-[15px] font-semibold text-foreground first:mt-0">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="my-2.5 text-[15px] leading-[1.66] text-foreground first:mt-0 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-2 ml-4 list-disc space-y-1 text-[15px] text-foreground">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="alfie-chat-steps my-2.5 space-y-2 text-[15px] text-foreground">{children}</ol>
  ),
};

// ─── Chip styles (citation chip presentation, design "2a") ───
const CHIP_BASE =
  'inline-flex items-center gap-1 align-middle rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap transition-colors';
const CHIP_TINT = 'bg-primary/10 text-primary';
const CHIP_TINT_HOVER = 'hover:bg-primary/15';

/** Render a matched resolved citation as a compact TYPE chip. The title lives
 *  once in the surrounding prose; the chip carries the type word, the link,
 *  and the full title in the tooltip / aria-label. `label` is already
 *  resolved by the caller (live label > token label). */
function renderResolved(citation: ResolvedCitation, label: string): React.ReactElement | null {
  if (citation.state === 'unavailable') {
    // Never a link — the target can't be resolved for this viewer.
    return <span className="text-muted-foreground">{label}</span>;
  }

  const typeLabel = ENTITY_TYPE_LABELS[citation.entityType] ?? 'Resource';
  const TypeIcon = ENTITY_TYPE_CHIP_ICONS[citation.entityType] ?? FileText;
  // The chip shows the resource TITLE (type word only as a fallback when the
  // citation carries no label); the type icon + tooltip carry the kind.
  const chipText = label || typeLabel;
  const chipContent = (
    <>
      <TypeIcon className="w-3 h-3 flex-shrink-0" aria-hidden />
      <span className="max-w-[240px] truncate">{chipText}</span>
    </>
  );
  const aria = label ? `${label} (${typeLabel})` : typeLabel;

  if (citation.state === 'locked') {
    return (
      <span
        title={label ? `Upgrade to access - ${label}` : 'Upgrade to access'}
        className={`${CHIP_BASE} bg-foreground/[0.06] text-muted-foreground`}
      >
        <Lock className="w-3 h-3 flex-shrink-0" aria-hidden />
        <span className="max-w-[240px] truncate">{chipText}</span>
      </span>
    );
  }

  // state === 'available'
  const kind = classifyUrl(citation.url);
  if (kind === 'internal') {
    return (
      <Link
        to={citation.url as string}
        title={label || undefined}
        aria-label={aria}
        className={`${CHIP_BASE} ${CHIP_TINT} ${CHIP_TINT_HOVER}`}
      >
        {chipContent}
      </Link>
    );
  }
  if (kind === 'external') {
    return (
      <a
        href={citation.url as string}
        target="_blank"
        rel="noopener noreferrer"
        title={label || undefined}
        aria-label={aria}
        className={`${CHIP_BASE} ${CHIP_TINT} ${CHIP_TINT_HOVER}`}
      >
        {chipContent}
      </a>
    );
  }
  // available but no (safe) url — expert / chip case.
  return (
    <span title={label || undefined} aria-label={aria} className={`${CHIP_BASE} ${CHIP_TINT}`}>
      {chipContent}
    </span>
  );
}

// ─── rehype plugin: rewrite `[[…]]` tokens into <citation> nodes ──
// Operates on the parsed HTML tree so it can skip code / links: only TEXT
// nodes that are NOT inside <code>, <pre> or <a> are tokenised. Each match
// becomes an element `<citation>` whose `data-*` properties carry the parsed
// token, which the component map (below) resolves.
const SKIP_TAGS = new Set(['code', 'pre', 'a']);

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

type ParsedToken =
  | { kind: 'cite' }
  | { kind: 'typed'; entityType: string; entityId: string; label?: string }
  | { kind: 'legacy'; name: string };

/** Classify one token's inner text (already trimmed of outer whitespace). */
function classifyToken(inner: string): ParsedToken {
  if (CITE_INNER_RE.test(inner)) return { kind: 'cite' };
  const m = TYPED_INNER_RE.exec(inner);
  if (m) {
    const entityType = ALIAS_TO_ENTITY_TYPE[m[1].toLowerCase()];
    const label = m[3]?.trim();
    return { kind: 'typed', entityType, entityId: m[2], label: label || undefined };
  }
  return { kind: 'legacy', name: inner };
}

// The model writes a citation after a space and before the sentence's
// punctuation: "…wasn't included [[cite:1]]. Keep…". Both neighbours travel
// WITH the token so the renderer can place them:
//  - the closing punctuation right after it (TRAIL) is glued to a chip, which
//    is an atomic inline the browser will otherwise happily break after,
//    leaving a lone "." at the start of the next line;
//  - the whitespace right before it (LEAD) is dropped when the token renders
//    nothing (a mid-stream `[[cite:N]]`), which otherwise left "included ."
//    with a gap before the full stop.
const TRAIL_PUNCT_RE = /^[.,;:!?…)\]}"'’”]+/;
const LEAD_SPACE_RE = /\s+$/;
// A letter or digit straight after the token (and its punctuation) means the
// space before the token is the only thing separating two words, so it stays.
const WORD_START_RE = /^[\p{L}\p{N}]/u;

function splitCitationText(value: string, hasTyped: boolean): HastNode[] {
  const out: HastNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  CITATION_RE.lastIndex = 0;
  while ((m = CITATION_RE.exec(value)) !== null) {
    const end = m.index + m[0].length;
    const trail = TRAIL_PUNCT_RE.exec(value.slice(end))?.[0] ?? '';
    let before = value.slice(last, m.index);
    const lead = WORD_START_RE.test(value.slice(end + trail.length))
      ? ''
      : (LEAD_SPACE_RE.exec(before)?.[0] ?? '');
    before = before.slice(0, before.length - lead.length);
    if (before) out.push({ type: 'text', value: before });
    const neighbours: Record<string, unknown> = {
      ...(lead ? { dataLead: lead } : {}),
      ...(trail ? { dataTrail: trail } : {}),
    };
    const inner = m[1].trim();
    const token = classifyToken(inner);
    // In v2 mode (message has a typed token) a legacy-looking token never uses
    // the name-search fallback — it renders as plain text instead.
    const properties: Record<string, unknown> =
      token.kind === 'legacy' && hasTyped
        ? { dataKind: 'plain', dataCitationName: token.name }
        : token.kind === 'cite'
          ? { dataKind: 'cite' }
          : token.kind === 'typed'
            ? {
                dataKind: 'typed',
                dataEntityType: token.entityType,
                dataEntityId: token.entityId,
                ...(token.label !== undefined ? { dataLabel: token.label } : {}),
              }
            : { dataKind: 'legacy', dataCitationName: token.name };
    out.push({
      type: 'element',
      tagName: 'citation',
      properties: { ...properties, ...neighbours },
      children: [],
    });
    // The trail is punctuation, so skipping it can never skip a `[[`.
    last = end + trail.length;
    CITATION_RE.lastIndex = last;
  }
  if (last < value.length) out.push({ type: 'text', value: value.slice(last) });
  return out;
}

function makeRehypeCitationTokens(hasTyped: boolean) {
  const walk = (node: HastNode, inSkip: boolean) => {
    if (!node || !Array.isArray(node.children)) return;
    const next: HastNode[] = [];
    for (const child of node.children) {
      if (child.type === 'text' && !inSkip && (child.value ?? '').includes('[[')) {
        next.push(...splitCitationText(child.value ?? '', hasTyped));
      } else {
        if (child.type === 'element') {
          walk(child, inSkip || SKIP_TAGS.has(child.tagName ?? ''));
        }
        next.push(child);
      }
    }
    node.children = next;
  };
  // Returns a unified Plugin (a factory that yields the Transformer). `tree` is
  // typed loosely so the plugin stays assignable to PluggableList (whose
  // Transformer expects the broad unist Node).
  return () => (tree: unknown) => { walk(tree as HastNode, false); };
}

// ─── Main component ──────────────────────────────────────────
/**
 * Renders Alfie / model markdown via the shared themed Markdown renderer,
 * extended with the citation-token layer (Citation Wire Contract v2).
 *
 * Rendering per state, for a TYPED token matched by (entityType, entityId).
 * All matched states render as a compact chip showing the resource TITLE
 * (truncated at 240px; the type word is the fallback text when the citation
 * has no label). The type icon + title/aria-label carry the kind:
 *  - available + internal url  → chip as in-app <Link>
 *  - available + external url  → chip as new-tab <a rel="noopener noreferrer">
 *  - available + null url      → inert chip — expert / no-page case
 *  - locked                    → muted chip with a lock glyph + "Upgrade to access" tooltip
 *  - unavailable               → plain muted text (NEVER a link, no chip)
 *  - no matching citation      → the token label as PLAIN TEXT (never a link)
 * Tooltip label preference: resolved live label > token label > nothing.
 *
 * `[[cite:N]]` transitional tokens render as nothing. A LEGACY message (no
 * typed token anywhere) keeps the old `[[Name]]` behaviour exactly, including
 * the /assets?search= fallback and the web-address guard. `assets` powers only
 * that legacy fallback path.
 */
export default function AlfieContent({
  content,
  citations,
  assets = [],
  variant = 'default',
}: {
  content: string;
  citations?: ResolvedCitation[];
  assets?: AssetRef[];
  /** 'chat' = full-page Alfie chat scale (see CHAT_SCALE_COMPONENTS). */
  variant?: 'default' | 'chat';
}) {
  // v2 lookup: (entityType, entityId) → citation. Keyed lowercase so a
  // mixed-case uuid or type in a token still matches.
  const citationByKey = React.useMemo(() => {
    const map = new Map<string, ResolvedCitation>();
    for (const c of citations ?? []) {
      if (c.entityType && c.entityId) {
        map.set(`${c.entityType}|${c.entityId}`.toLowerCase(), c);
      }
    }
    return map;
  }, [citations]);

  // Legacy lookup: name → citation (only used by legacy-mode messages).
  const citationByName = React.useMemo(() => {
    const map = new Map<string, ResolvedCitation>();
    for (const c of citations ?? []) map.set(c.name.trim().toLowerCase(), c);
    return map;
  }, [citations]);

  const hasTyped = React.useMemo(() => HAS_TYPED_TOKEN_RE.test(content ?? ''), [content]);

  const rehypePlugins = React.useMemo(() => [makeRehypeCitationTokens(hasTyped)], [hasTyped]);

  const findAsset = React.useCallback(
    (name: string): AssetRef | undefined => {
      const lower = name.toLowerCase();
      return (
        assets.find((a) => a.title.toLowerCase() === lower) ??
        assets.find(
          (a) =>
            a.title.toLowerCase().includes(lower) || lower.includes(a.title.toLowerCase()),
        )
      );
    },
    [assets],
  );

  const components = React.useMemo(() => {
    /** What a token renders as, and whether that is an atomic inline (a chip
     *  or an inline-flex link) rather than plain text. */
    const resolveToken = (
      props: Record<string, unknown>,
    ): { el: React.ReactElement | null; atomic: boolean } => {
      const kind = String(props.dataKind ?? 'legacy');

      // Transitional streaming token — never visible.
      if (kind === 'cite') return { el: null, atomic: false };

      // Legacy-looking token inside a v2 message — plain text, no fallback.
      if (kind === 'plain') {
        const name = String(props.dataCitationName ?? '');
        return { el: name ? <span>{name}</span> : null, atomic: false };
      }

      if (kind === 'typed') {
        const entityType = String(props.dataEntityType ?? '');
        const entityId = String(props.dataEntityId ?? '');
        const tokenLabel = props.dataLabel != null ? String(props.dataLabel) : '';
        const citation = citationByKey.get(`${entityType}|${entityId}`.toLowerCase());
        // No matching resolved citation → token label as PLAIN TEXT. Never a
        // link, never a name-search fallback.
        if (!citation) {
          return { el: tokenLabel ? <span>{tokenLabel}</span> : null, atomic: false };
        }
        const label = (citation.label || tokenLabel).trim();
        return { el: renderResolved(citation, label), atomic: citation.state !== 'unavailable' };
      }

      // kind === 'legacy' — pre-v2 behaviour, unchanged.
      const name = String(props.dataCitationName ?? '').trim();
      const citation = citationByName.get(name.toLowerCase());

      // Legacy / unmatched → old name-search fallback.
      if (!citation || citation.legacy) {
        const asset = findAsset(name);
        // A web address with no matching library asset is not an asset
        // citation — render it as plain text, never an asset-search link.
        if (!asset && looksLikeWebAddress(name)) {
          return { el: <span>{name}</span>, atomic: false };
        }
        return { el: <AssetInlineLink name={name} asset={asset} />, atomic: true };
      }

      const label = citation.label || citation.name || name;
      return { el: renderResolved(citation, label), atomic: citation.state !== 'unavailable' };
    };

    const Citation = ({ node }: { node?: HastNode }) => {
      const props = node?.properties ?? {};
      const lead = String(props.dataLead ?? '');
      const trail = String(props.dataTrail ?? '');
      const { el, atomic } = resolveToken(props);
      // Nothing to show: the space before the token goes with it, so a
      // mid-stream "included [[cite:1]]." reads "included." rather than
      // "included .". The sentence's punctuation always stays.
      if (!el) return trail ? <>{trail}</> : null;
      // Plain text breaks like the prose around it; only an atomic chip needs
      // its trailing punctuation held on the same line.
      if (!trail || !atomic) return <>{lead}{el}{trail}</>;
      return (
        <>
          {lead}
          <span className="whitespace-nowrap">
            {el}
            {trail}
          </span>
        </>
      );
    };
    // `citation` is a custom tag emitted by the rehype plugin — not a known
    // intrinsic element, so cast past react-markdown's tag-keyed Components type.
    return {
      ...(variant === 'chat' ? CHAT_SCALE_COMPONENTS : {}),
      citation: Citation,
    } as unknown as Components;
  }, [citationByKey, citationByName, findAsset, variant]);

  if (!content) return null;

  return (
    <Markdown rehypePlugins={rehypePlugins} components={components}>
      {content}
    </Markdown>
  );
}
